'use client';

import { useLazyQuery } from '@apollo/client';
import { signOut, useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { destroyCookie } from 'nookies';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { destroyAuthConditionCookies } from './auth-cookies';
import { MigrationModals } from './migration-modals';
import type { AuthConditions, AuthContextType } from './types';
import { useIdpMigration } from './use-idp-migration';
import { useMagic } from './use-magic';
import { GetMeIsEmailVerifiedDocument } from '@/__generated__/graphql';
import { PostSignOutAPI, PostSignInAPI } from '@/apis';
import { LoadingInner } from '@/components/ui/loading/Loading';
import { PATHS, QUERIES } from '@/constants/path';
import { usePathname, useRouter } from '@/i18n/routing';
import { resetIdpTokenGate } from '@/lib/idp-token-gate';
import useDialog from '@/providers/dialog-provider';
import { setCookie } from '@/utils/cookie';
import { localStorage } from '@/utils/local-storage';

export type { AuthConditions, IdpResolveOrigin, Magic, MigrationSubmitValues } from './types';

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuthenticationContext = () => useContext(AuthContext);

export const AuthProvider = ({
  children,
  authConditions,
  isBot,
}: {
  children: ReactNode;
  authConditions: AuthConditions;
  isBot: boolean;
}) => {
  const t = useTranslations();
  const pathname = usePathname();
  const { data: idpSession, status: idpSessionStatus } = useSession();
  // refresh に失敗し access_token を破棄したセッション（error 付き）は、
  // 「IdP認証済みだが access_token 無し」ではなく「IdP認証失敗（未認証扱い）」とする。
  // これにより me フォールバックやログアウト分岐が IdP 認証済みとして走らず、再ログインへ倒れる。
  const isIdpAuthenticated = idpSessionStatus === 'authenticated' && !idpSession?.error;
  const [idpPostLogoutUrl, setIdpPostLogoutUrl] = useState<string | null>(null);
  const [isInitializationComplete, setIsInitializationComplete] = useState(false);
  const [isRegistered, setIsRegistered] = useState(authConditions.isRegistered === 'true');
  const [isEmailVerified, setIsEmailVerified] = useState(authConditions.isEmailVerified === 'true');
  const [getMeIsEmailVerified] = useLazyQuery(GetMeIsEmailVerifiedDocument, {
    onCompleted: (data) => {
      if (data.me?.isEmailVerified) {
        setIsRegistered(true);
        setIsEmailVerified(true);
      }
    },
  });
  const { showDialog } = useDialog();
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [isEmailUpdating, setIsEmailUpdating] = useState(false);
  const { push } = useRouter();
  const [selectedArtistOfficialAccountId, setSelectedArtistOfficialAccountId] = useState(
    authConditions.selectedArtistOfficialAccountId,
  );

  const isArtistLoggedIn = isRegistered && selectedArtistOfficialAccountId !== null;
  const {
    magicLogin,
    magicAuthReady,
    magicLogout,
    token,
    magic,
    magicAuthenticated,
    magicUpdateEmail,
    magicExportPrivateKey,
  } = useMagic({
    isRegistered,
    isBot,
  });

  const migration = useIdpMigration({
    isRegistered,
    isIdpAuthenticated,
    idpSessionStatus,
    isInitializationComplete,
    magicAuthReady,
    magicAuthenticated,
    token,
    magicLogout,
    setIsRegistered,
    setIsEmailVerified,
    setIsLoginLoading,
    getMeIsEmailVerified,
  });

  async function getMagicEmailAddress() {
    return (await magic?.user.getInfo())?.email;
  }

  // メールアドレス変更
  async function updateEmail(): Promise<void> {
    try {
      setIsEmailUpdating(true);
      await magicUpdateEmail();
      await push(PATHS.SETTINGS);
    } catch (error) {
      console.error(error);
    } finally {
      setIsEmailUpdating(false);
    }
  }

  // 秘密鍵エクスポート
  async function exportPrivateKey(): Promise<void> {
    if (magic && (await magic.user.isLoggedIn())) {
      await magicLogout();
      destroyAuthConditionCookies();
      destroyCookie(null, 'HAS_LOGGED_IN', { path: '/' });
      setIsRegistered(false);
      setIsEmailVerified(false);
    }
    await magicExportPrivateKey();
  }

  async function redirectAfterLoginWithWelcomeModal() {
    try {
      const callbackUrl = localStorage('callbackUrl').getValue();
      const { ok, isEmailVerified, isRegistered } = await PostSignInAPI(token);
      if (ok) {
        setIsRegistered(isRegistered);
        setIsEmailVerified(isEmailVerified);
        localStorage('didAbandonProfileInputPage').setValue('false');
      }
      push(
        `${callbackUrl || PATHS.ROOT}?${QUERIES.WELCOME_MODAL.key}=${QUERIES.WELCOME_MODAL.value}`,
      );
    } catch (error) {
      console.error(error);
    }
  }

  async function clearStorageOnboardingAndProfile() {
    localStorage('didAbandonProfileInputPage').setValue('false');
  }

  async function login() {
    try {
      setIsLoginLoading(true);

      migration.resetMigrationResolveHandled();
      destroyAuthConditionCookies();
      await magicLogout();
      setIsRegistered(false);
      setIsEmailVerified(false);
      await magicLogin();
      localStorage('callbackUrl').setValue(pathname);
    } catch (error) {
      console.error(error);
      setIsLoginLoading(false);
    }
  }

  async function logout(isDeleteUser = false) {
    resetIdpTokenGate();
    // IdP(NextAuth/OIDC)でログイン中は Magic セッションが存在しないため、
    // magic.user.getIdToken() を呼ぶと「User denied account access」エラーになる。
    // idp-logout ルートで Hydra の RP-initiated logout URL(id_token_hint 付き)を取得し
    // refresh_token を失効させたうえで、NextAuth セッションは即時 signOut で破棄する。
    // access_token Cookie(__Secure-klew-access-token)は遷移時に proxy.ts の middleware が削除する。
    if (isIdpAuthenticated) {
      try {
        destroyAuthConditionCookies();
        destroyCookie(null, 'HAS_LOGGED_IN', {
          path: '/',
        });
        clearStorageOnboardingAndProfile();

        // id_token_hint はサーバー側にしかないため、signOut でセッションを消す前に取得する。
        // 取得に失敗しても後続の signOut は必ず実行する（削除済みアカウントのセッションを残さない）。
        let idpLogoutUrl: string | undefined;
        try {
          const res = await fetch('/frontend-api/auth/idp-logout', { method: 'POST' });
          if (res.ok) idpLogoutUrl = ((await res.json()) as { url?: string }).url;
        } catch (error) {
          console.error(error);
        }

        // NextAuth セッションを即時破棄する（削除フローで離脱してもトークンを残さない）。
        await signOut({ redirect: false });

        const target = idpLogoutUrl ?? process.env.NEXT_PUBLIC_BASE_URL;
        if (isDeleteUser) {
          // 削除フローは完了モーダル確定時に遷移するため、遷移先 URL を保持する。
          setIdpPostLogoutUrl(target);
        } else {
          window.location.assign(target);
        }
      } catch (error) {
        console.error(error);
        showDialog({
          title: t('common-error-title'),
          body: t('login-error-description'),
        });
      }
      return;
    }

    try {
      const didToken = await magic?.user.getIdToken();
      if (didToken) {
        await PostSignOutAPI(didToken);
        await magicLogout();
        destroyCookie(null, 'HAS_LOGGED_IN', {
          path: '/',
        });
        clearStorageOnboardingAndProfile();
        if (!isDeleteUser) window.location.assign(process.env.NEXT_PUBLIC_BASE_URL);
      }
    } catch (error) {
      console.error(error);
      showDialog({
        title: t('common-error-title'),
        body: t('login-error-description'),
      });
    }
  }

  useEffect(() => {
    if (isRegistered) {
      setCookie('HAS_LOGGED_IN', {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      getMeIsEmailVerified();
    }
  }, [isRegistered]);

  useEffect(() => {
    (async () => {
      const didAbandonProfileInputPage =
        localStorage('didAbandonProfileInputPage').getValue() === 'true';

      if (magicAuthReady) {
        const urls: string[] = [PATHS.SIGN_UP, PATHS.SIGN_UP_PROFILE];
        const isSignInProcessPage = urls.includes(pathname);
        if (
          !isRegistered &&
          magicAuthenticated &&
          didAbandonProfileInputPage &&
          !isSignInProcessPage
        ) {
          await magicLogout();
          localStorage('didAbandonProfileInputPage').setValue('false');
        }
        setIsInitializationComplete(true);
      }
    })();
  }, [magicAuthReady, pathname]);

  const value = {
    isArtistLoggedIn,
    isEmailVerified,
    isIdpAuthenticated,
    idpPostLogoutUrl,
    isRegistered,
    login,
    loginDisabled: !magicAuthReady || isRegistered || isLoginLoading,
    logout,
    magicAuthenticated,
    magicAuthReady,
    redirectAfterLoginWithWelcomeModal,
    selectedArtistOfficialAccountId,
    setSelectedArtistOfficialAccountId,
    token,
    getMagicEmailAddress,
    startIdpResolve: migration.startIdpResolve,
    updateEmail,
    exportPrivateKey,
  };

  return (
    <AuthContext.Provider value={value}>
      {(isLoginLoading || isEmailUpdating) && (
        <div className='fixed left-0 top-0 z-50 flex h-lvh w-full justify-center'>
          <div className='absolute flex size-full md:relative md:w-md-base lg:w-lg-base'>
            <div className='shrink-0 md:hidden lg:block lg:w-[20rem]' />
            <LoadingInner dark />
          </div>
        </div>
      )}
      {children}
      <MigrationModals {...migration} />
    </AuthContext.Provider>
  );
};
